import React, { useState, useEffect, useMemo } from 'react'
import { getLocalApiUrl, authFetch } from '../../config/api'
import {
  CogIcon,
  PlusIcon,
  PencilIcon,
  TrashIcon,
  BuildingOfficeIcon,
  AdjustmentsHorizontalIcon,
  ArrowPathIcon,
  ChevronDownIcon,
  ChevronRightIcon,
  XMarkIcon,
  MagnifyingGlassIcon,
  ShieldCheckIcon,
  GlobeAltIcon,
  DocumentTextIcon,
  ChartBarIcon,
  UserGroupIcon,
  ExclamationTriangleIcon,
  ClockIcon,
  BanknotesIcon,
  EyeIcon,
  EyeSlashIcon,
  LockClosedIcon,
  SparklesIcon,
  RocketLaunchIcon,
  CheckIcon,
  ChatBubbleLeftRightIcon,
} from '@heroicons/react/24/outline'

const TIERS = ['standard', 'professional', 'enterprise']
const PHASES = ['alpha', 'beta', 'ga']
const SCOPES = ['module', 'page', 'feature']

const TIER_COLORS = {
  standard: 'bg-gray-100 text-gray-700 border-gray-200',
  professional: 'bg-blue-50 text-blue-700 border-blue-200',
  enterprise: 'bg-purple-50 text-purple-700 border-purple-200',
}
const TIER_BADGE = {
  standard: 'bg-gray-500',
  professional: 'bg-blue-500',
  enterprise: 'bg-purple-500',
}
const PHASE_COLORS = {
  alpha: 'bg-red-50 text-red-700',
  beta: 'bg-amber-50 text-amber-700',
  ga: 'bg-emerald-50 text-emerald-700',
}
const PHASE_ICONS = {
  alpha: ExclamationTriangleIcon,
  beta: SparklesIcon,
  ga: CheckIcon,
}

const MODULE_ICONS = {
  'executive': ChartBarIcon,
  'profile-safe': UserGroupIcon,
  'monitor-safe': ShieldCheckIcon,
  'compliance-safe': DocumentTextIcon,
  'payout-safe': BanknotesIcon,
  'governance-safe': ClockIcon,
  'archie': ChatBubbleLeftRightIcon,
  'admin': CogIcon,
}
const MODULE_COLORS = {
  'executive': 'from-amber-500 to-orange-500',
  'profile-safe': 'from-blue-500 to-cyan-500',
  'monitor-safe': 'from-emerald-500 to-teal-500',
  'compliance-safe': 'from-violet-500 to-purple-500',
  'payout-safe': 'from-rose-500 to-pink-500',
  'governance-safe': 'from-indigo-500 to-blue-500',
  'archie': 'from-teal-500 to-emerald-500',
  'admin': 'from-gray-500 to-slate-500',
}

// Toggle switch component
function Toggle({ enabled, onChange, disabled, size = 'md' }) {
  const sizes = {
    sm: { track: 'h-4 w-7', thumb: 'h-3 w-3', translate: 'translate-x-3' },
    md: { track: 'h-5 w-9', thumb: 'h-4 w-4', translate: 'translate-x-4' },
    lg: { track: 'h-6 w-11', thumb: 'h-5 w-5', translate: 'translate-x-5' },
  }
  const s = sizes[size]
  return (
    <button
      type="button"
      onClick={() => !disabled && onChange(!enabled)}
      className={`relative inline-flex flex-shrink-0 ${s.track} border-2 border-transparent rounded-full cursor-pointer transition-colors ease-in-out duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 ${
        disabled ? 'opacity-50 cursor-not-allowed' : ''
      } ${enabled ? 'bg-indigo-600' : 'bg-gray-200'}`}
      disabled={disabled}
    >
      <span className={`pointer-events-none inline-block ${s.thumb} rounded-full bg-white shadow transform ring-0 transition ease-in-out duration-200 ${
        enabled ? s.translate : 'translate-x-0'
      }`} />
    </button>
  )
}

export default function PlatformConfiguration() {
  const [activeTab, setActiveTab] = useState('flags')
  const [flags, setFlags] = useState([])
  const [tenants, setTenants] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [searchTerm, setSearchTerm] = useState('')

  // Filters
  const [tierFilter, setTierFilter] = useState('')
  const [phaseFilter, setPhaseFilter] = useState('')
  const [showDisabledOnly, setShowDisabledOnly] = useState(false)

  // Flag editor
  const [editingFlag, setEditingFlag] = useState(null)
  const [flagForm, setFlagForm] = useState({})

  // Tenant detail
  const [selectedTenant, setSelectedTenant] = useState(null)
  const [tenantFlags, setTenantFlags] = useState(null)
  const [tenantSearch, setTenantSearch] = useState('')

  // Module expansion (for page-level flags within a module)
  const [expandedModules, setExpandedModules] = useState({})

  useEffect(() => {
    loadFlags()
    loadTenants()
  }, [])

  const loadFlags = async () => {
    try {
      setLoading(true)
      const res = await authFetch(getLocalApiUrl('/api/admin/feature-flags'))
      const data = await res.json()
      if (data.success) setFlags(data.flags || [])
      else setError(data.error)
    } catch (err) {
      setError('Failed to load feature flags')
    } finally {
      setLoading(false)
    }
  }

  const loadTenants = async () => {
    try {
      const res = await authFetch(getLocalApiUrl('/api/admin/feature-flags/tenants'))
      const data = await res.json()
      if (data.success) setTenants(data.tenants || [])
    } catch (_) {}
  }

  const loadTenantDetail = async (groupId) => {
    try {
      const res = await authFetch(getLocalApiUrl(`/api/admin/feature-flags/tenants/${groupId}/resolve`))
      const data = await res.json()
      if (data.success) setTenantFlags(data)
    } catch (_) {}
  }

  // ── Flag mutations ──
  const toggleFlagDefault = async (flag) => {
    try {
      await authFetch(getLocalApiUrl(`/api/admin/feature-flags/${encodeURIComponent(flag.flag_key)}`), {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ enabled_default: !flag.enabled_default }),
      })
      loadFlags()
    } catch (_) {}
  }

  const updateFlagField = async (flagKey, field, value) => {
    try {
      await authFetch(getLocalApiUrl(`/api/admin/feature-flags/${encodeURIComponent(flagKey)}`), {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ [field]: value }),
      })
      loadFlags()
    } catch (_) {}
  }

  const saveFlag = async () => {
    try {
      const isNew = !editingFlag || editingFlag === 'new'
      const url = isNew ? '/api/admin/feature-flags' : `/api/admin/feature-flags/${encodeURIComponent(flagForm.flagKey)}`
      const res = await authFetch(getLocalApiUrl(url), {
        method: isNew ? 'POST' : 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(flagForm),
      })
      const data = await res.json()
      if (!data.success) { setError(data.error); return }
      setEditingFlag(null)
      setFlagForm({})
      loadFlags()
    } catch (err) { setError('Failed to save flag') }
  }

  const deleteFlag = async (flagKey) => {
    if (!confirm(`Delete "${flagKey}"? All tenant overrides will also be removed.`)) return
    await authFetch(getLocalApiUrl(`/api/admin/feature-flags/${encodeURIComponent(flagKey)}`), { method: 'DELETE' })
    loadFlags()
  }

  // ── Tenant mutations ──
  const setTenantTier = async (groupId, tier) => {
    await authFetch(getLocalApiUrl(`/api/admin/feature-flags/tenants/${groupId}/tier`), {
      method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ tier }),
    })
    loadTenants()
    if (selectedTenant?.group_id === groupId) loadTenantDetail(groupId)
  }

  const toggleTenantOverride = async (groupId, flagKey, currentEnabled) => {
    await authFetch(getLocalApiUrl(`/api/admin/feature-flags/tenants/${groupId}/overrides/${encodeURIComponent(flagKey)}`), {
      method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ enabled: !currentEnabled }),
    })
    loadTenantDetail(groupId)
  }

  const removeTenantOverride = async (groupId, flagKey) => {
    await authFetch(getLocalApiUrl(`/api/admin/feature-flags/tenants/${groupId}/overrides/${encodeURIComponent(flagKey)}`), { method: 'DELETE' })
    loadTenantDetail(groupId)
  }

  // ── Derived data ──
  const moduleFlags = useMemo(() => flags.filter(f => f.scope === 'module'), [flags])
  const pageFlags = useMemo(() => flags.filter(f => f.scope === 'page'), [flags])
  const featureFlags = useMemo(() => flags.filter(f => f.scope === 'feature'), [flags])

  const filteredPageFlags = useMemo(() => {
    return pageFlags.filter(f => {
      if (tierFilter && f.tier !== tierFilter) return false
      if (phaseFilter && f.phase !== phaseFilter) return false
      if (showDisabledOnly && f.enabled_default) return false
      if (searchTerm) {
        const term = searchTerm.toLowerCase()
        if (!f.flag_key.toLowerCase().includes(term) && !f.name.toLowerCase().includes(term)) return false
      }
      return true
    })
  }, [pageFlags, tierFilter, phaseFilter, showDisabledOnly, searchTerm])

  // Group page flags by module
  const pagesByModule = useMemo(() => {
    const groups = {}
    for (const f of filteredPageFlags) {
      const mod = f.module || 'other'
      if (!groups[mod]) groups[mod] = []
      groups[mod].push(f)
    }
    return groups
  }, [filteredPageFlags])

  // Tenant flags grouped for the tenant tab
  const tenantFlagsByModule = useMemo(() => {
    if (!tenantFlags?.flagDetails) return {}
    const groups = {}
    for (const f of tenantFlags.flagDetails) {
      if (tenantSearch) {
        const term = tenantSearch.toLowerCase()
        if (!f.flagKey.toLowerCase().includes(term) && !f.name.toLowerCase().includes(term)) continue
      }
      const mod = f.module || 'other'
      if (!groups[mod]) groups[mod] = []
      groups[mod].push(f)
    }
    return groups
  }, [tenantFlags, tenantSearch])

  const enabledCount = useMemo(() => flags.filter(f => f.enabled_default).length, [flags])

  if (loading && flags.length === 0) {
    return <div className="flex items-center justify-center h-64"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div></div>
  }

  return (
    <div className="max-w-7xl mx-auto">
      {/* Header */}
      <div className="mb-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
              <AdjustmentsHorizontalIcon className="h-7 w-7 text-indigo-600" />
              Platform Configuration
            </h1>
            <p className="mt-1 text-sm text-gray-500">
              Control which modules, pages, and features are available across the platform.
            </p>
          </div>
          <div className="flex items-center gap-3">
            <div className="text-right">
              <div className="text-2xl font-bold text-gray-900">{enabledCount}/{flags.length}</div>
              <div className="text-xs text-gray-500">flags enabled</div>
            </div>
            <button onClick={loadFlags} className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-md">
              <ArrowPathIcon className="h-5 w-5" />
            </button>
          </div>
        </div>
      </div>

      {error && (
        <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-md text-red-700 text-sm flex items-center justify-between">
          <span>{error}</span>
          <button onClick={() => setError(null)} className="text-red-500 hover:text-red-700"><XMarkIcon className="h-4 w-4" /></button>
        </div>
      )}

      {/* Tabs */}
      <div className="border-b border-gray-200 mb-6">
        <nav className="-mb-px flex space-x-8">
          {[
            { key: 'flags', label: 'Feature Flags', desc: 'Platform defaults' },
            { key: 'tenants', label: 'Tenant Overrides', desc: 'Per-tenant configuration' },
          ].map(tab => (
            <button
              key={tab.key}
              onClick={() => { setActiveTab(tab.key); setSelectedTenant(null); setTenantFlags(null) }}
              className={`py-3 px-1 border-b-2 text-sm font-medium transition-colors ${
                activeTab === tab.key
                  ? 'border-indigo-500 text-indigo-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <div>{tab.label}</div>
              <div className="text-xs font-normal">{tab.desc}</div>
            </button>
          ))}
        </nav>
      </div>

      {/* ══════════════════ Feature Flags Tab ══════════════════ */}
      {activeTab === 'flags' && (
        <div>
          {/* ── Module Cards ── */}
          <div className="mb-8">
            <h2 className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-3">Modules</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
              {moduleFlags.map(flag => {
                const Icon = MODULE_ICONS[flag.module] || GlobeAltIcon
                const gradient = MODULE_COLORS[flag.module] || 'from-gray-500 to-gray-600'
                const pageCount = pageFlags.filter(p => p.module === flag.module).length
                const enabledPages = pageFlags.filter(p => p.module === flag.module && p.enabled_default).length

                return (
                  <div key={flag.flag_key} className={`relative bg-white rounded-xl border-2 transition-all ${flag.enabled_default ? 'border-gray-200 shadow-sm' : 'border-gray-200 opacity-60'}`}>
                    <div className={`h-1.5 rounded-t-xl bg-gradient-to-r ${gradient} ${!flag.enabled_default ? 'opacity-30' : ''}`} />
                    <div className="p-4">
                      <div className="flex items-start justify-between mb-3">
                        <div className="flex items-center gap-2">
                          <div className={`p-1.5 rounded-lg bg-gradient-to-br ${gradient} text-white`}>
                            <Icon className="h-4 w-4" />
                          </div>
                          <div>
                            <div className="text-sm font-semibold text-gray-900">{flag.name}</div>
                            <div className="text-xs text-gray-400">{flag.module}</div>
                          </div>
                        </div>
                        <Toggle enabled={flag.enabled_default} onChange={() => toggleFlagDefault(flag)} />
                      </div>

                      <div className="flex items-center justify-between mt-2">
                        <div className="flex items-center gap-2">
                          <span className={`inline-flex items-center px-2 py-0.5 text-xs font-medium rounded-full border ${TIER_COLORS[flag.tier]}`}>
                            {flag.tier}
                          </span>
                          <span className={`inline-flex items-center px-1.5 py-0.5 text-xs rounded-full ${PHASE_COLORS[flag.phase]}`}>
                            {flag.phase}
                          </span>
                        </div>
                        <div className="text-xs text-gray-400">
                          {enabledPages}/{pageCount} pages
                        </div>
                      </div>
                    </div>
                  </div>
                )
              })}
            </div>
          </div>

          {/* ── Page-Level Flags ── */}
          <div className="mb-8">
            <div className="flex items-center justify-between mb-3">
              <h2 className="text-sm font-semibold text-gray-500 uppercase tracking-wide">Pages & Features</h2>
              <div className="flex items-center gap-2">
                <div className="relative">
                  <MagnifyingGlassIcon className="h-4 w-4 absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400" />
                  <input type="text" placeholder="Search..." value={searchTerm} onChange={e => setSearchTerm(e.target.value)}
                    className="pl-8 pr-3 py-1.5 text-sm border border-gray-300 rounded-lg w-48 focus:ring-indigo-500 focus:border-indigo-500" />
                </div>
                <select value={tierFilter} onChange={e => setTierFilter(e.target.value)} className="text-xs border-gray-300 rounded-lg py-1.5">
                  <option value="">All Tiers</option>
                  {TIERS.map(t => <option key={t} value={t}>{t}</option>)}
                </select>
                <select value={phaseFilter} onChange={e => setPhaseFilter(e.target.value)} className="text-xs border-gray-300 rounded-lg py-1.5">
                  <option value="">All Phases</option>
                  {PHASES.map(p => <option key={p} value={p}>{p}</option>)}
                </select>
                <button onClick={() => setShowDisabledOnly(!showDisabledOnly)}
                  className={`inline-flex items-center gap-1 px-2.5 py-1.5 text-xs rounded-lg border transition-colors ${
                    showDisabledOnly ? 'bg-red-50 border-red-200 text-red-700' : 'bg-white border-gray-300 text-gray-600 hover:bg-gray-50'
                  }`}>
                  {showDisabledOnly ? <EyeSlashIcon className="h-3.5 w-3.5" /> : <EyeIcon className="h-3.5 w-3.5" />}
                  {showDisabledOnly ? 'Disabled only' : 'Show all'}
                </button>
                <button onClick={() => { setEditingFlag('new'); setFlagForm({ scope: 'page', tier: 'standard', phase: 'ga', enabledDefault: true }) }}
                  className="inline-flex items-center gap-1 px-2.5 py-1.5 text-xs bg-indigo-600 text-white rounded-lg hover:bg-indigo-700">
                  <PlusIcon className="h-3.5 w-3.5" /> Add Flag
                </button>
              </div>
            </div>

            {/* Flag create/edit panel */}
            {editingFlag && (
              <div className="mb-4 p-4 bg-indigo-50 border border-indigo-200 rounded-xl">
                <h3 className="text-sm font-semibold text-indigo-900 mb-3">{editingFlag === 'new' ? 'Create New Flag' : 'Edit Flag'}</h3>
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
                  <div><label className="block text-xs text-gray-600 mb-0.5">Flag Key</label>
                    <input type="text" value={flagForm.flagKey || ''} onChange={e => setFlagForm({...flagForm, flagKey: e.target.value})} disabled={editingFlag !== 'new'} placeholder="page:module/page" className="w-full text-sm border-gray-300 rounded-lg disabled:bg-gray-100" /></div>
                  <div><label className="block text-xs text-gray-600 mb-0.5">Name</label>
                    <input type="text" value={flagForm.name || ''} onChange={e => setFlagForm({...flagForm, name: e.target.value})} className="w-full text-sm border-gray-300 rounded-lg" /></div>
                  <div><label className="block text-xs text-gray-600 mb-0.5">Module</label>
                    <input type="text" value={flagForm.module || ''} onChange={e => setFlagForm({...flagForm, module: e.target.value})} placeholder="e.g. profile-safe" className="w-full text-sm border-gray-300 rounded-lg" /></div>
                  <div><label className="block text-xs text-gray-600 mb-0.5">Scope</label>
                    <select value={flagForm.scope || 'page'} onChange={e => setFlagForm({...flagForm, scope: e.target.value})} className="w-full text-sm border-gray-300 rounded-lg">{SCOPES.map(s => <option key={s}>{s}</option>)}</select></div>
                  <div><label className="block text-xs text-gray-600 mb-0.5">Tier</label>
                    <select value={flagForm.tier || 'standard'} onChange={e => setFlagForm({...flagForm, tier: e.target.value})} className="w-full text-sm border-gray-300 rounded-lg">{TIERS.map(t => <option key={t}>{t}</option>)}</select></div>
                  <div><label className="block text-xs text-gray-600 mb-0.5">Phase</label>
                    <select value={flagForm.phase || 'ga'} onChange={e => setFlagForm({...flagForm, phase: e.target.value})} className="w-full text-sm border-gray-300 rounded-lg">{PHASES.map(p => <option key={p}>{p}</option>)}</select></div>
                  <div><label className="block text-xs text-gray-600 mb-0.5">Default</label>
                    <select value={flagForm.enabledDefault === false ? 'false' : 'true'} onChange={e => setFlagForm({...flagForm, enabledDefault: e.target.value === 'true'})} className="w-full text-sm border-gray-300 rounded-lg">
                      <option value="true">Enabled</option><option value="false">Disabled</option></select></div>
                </div>
                <div className="mt-3 flex gap-2">
                  <button onClick={saveFlag} className="px-4 py-1.5 bg-indigo-600 text-white text-sm rounded-lg hover:bg-indigo-700">Save</button>
                  <button onClick={() => { setEditingFlag(null); setFlagForm({}) }} className="px-4 py-1.5 bg-white text-gray-700 text-sm rounded-lg border border-gray-300 hover:bg-gray-50">Cancel</button>
                </div>
              </div>
            )}

            {/* Page flags grouped by module */}
            <div className="space-y-3">
              {Object.entries(pagesByModule).map(([mod, modFlags]) => {
                const Icon = MODULE_ICONS[mod] || GlobeAltIcon
                const isExpanded = expandedModules[mod] !== false // default expanded
                const enabledInMod = modFlags.filter(f => f.enabled_default).length

                return (
                  <div key={mod} className="bg-white rounded-xl border border-gray-200 overflow-hidden">
                    <button onClick={() => setExpandedModules(prev => ({...prev, [mod]: !isExpanded}))}
                      className="w-full flex items-center justify-between px-4 py-3 hover:bg-gray-50 transition-colors">
                      <div className="flex items-center gap-3">
                        {isExpanded ? <ChevronDownIcon className="h-4 w-4 text-gray-400" /> : <ChevronRightIcon className="h-4 w-4 text-gray-400" />}
                        <Icon className="h-5 w-5 text-gray-500" />
                        <span className="text-sm font-semibold text-gray-800 capitalize">{mod.replace('-', ' ')}</span>
                        <span className="text-xs text-gray-400">{enabledInMod}/{modFlags.length} enabled</span>
                      </div>
                    </button>

                    {isExpanded && (
                      <div className="border-t border-gray-100 divide-y divide-gray-50">
                        {modFlags.map(flag => {
                          const PhaseIcon = PHASE_ICONS[flag.phase] || CheckIcon
                          return (
                            <div key={flag.flag_key} className={`flex items-center justify-between px-4 py-2.5 hover:bg-gray-50 transition-colors ${!flag.enabled_default ? 'bg-gray-50/50' : ''}`}>
                              <div className="flex items-center gap-3 min-w-0 flex-1">
                                <Toggle enabled={flag.enabled_default} onChange={() => toggleFlagDefault(flag)} size="sm" />
                                <div className="min-w-0">
                                  <div className={`text-sm font-medium ${flag.enabled_default ? 'text-gray-900' : 'text-gray-400 line-through'}`}>{flag.name}</div>
                                  {flag.description && <div className="text-xs text-gray-400 truncate">{flag.description}</div>}
                                </div>
                              </div>
                              <div className="flex items-center gap-2 ml-4">
                                <span className={`inline-flex items-center gap-0.5 px-2 py-0.5 text-xs font-medium rounded-full border ${TIER_COLORS[flag.tier]}`}>
                                  {flag.tier}
                                </span>
                                <span className={`inline-flex items-center gap-0.5 px-1.5 py-0.5 text-xs rounded-full ${PHASE_COLORS[flag.phase]}`}>
                                  <PhaseIcon className="h-3 w-3" /> {flag.phase}
                                </span>
                                <div className="flex items-center gap-0.5 ml-1">
                                  <select value={flag.tier} onChange={e => updateFlagField(flag.flag_key, 'tier', e.target.value)}
                                    className="text-xs border-0 bg-transparent text-gray-400 cursor-pointer p-0 pr-4 focus:ring-0" title="Change tier">
                                    {TIERS.map(t => <option key={t} value={t}>{t}</option>)}
                                  </select>
                                  <button onClick={() => deleteFlag(flag.flag_key)} className="text-gray-300 hover:text-red-500 transition-colors" title="Delete">
                                    <TrashIcon className="h-3.5 w-3.5" />
                                  </button>
                                </div>
                              </div>
                            </div>
                          )
                        })}
                      </div>
                    )}
                  </div>
                )
              })}
            </div>

            {filteredPageFlags.length === 0 && !loading && (
              <div className="text-center py-12 text-gray-400">
                {flags.length === 0 ? 'No feature flags defined. Run: npm run seed:feature-flags' : 'No flags match the current filters.'}
              </div>
            )}
          </div>
        </div>
      )}

      {/* ══════════════════ Tenant Overrides Tab ══════════════════ */}
      {activeTab === 'tenants' && (
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
          {/* Tenant list */}
          <div className="lg:col-span-4">
            <div className="bg-white rounded-xl border border-gray-200 overflow-hidden sticky top-4">
              <div className="px-4 py-3 border-b border-gray-200 bg-gray-50">
                <h3 className="text-sm font-semibold text-gray-700">Tenants</h3>
              </div>
              <div className="divide-y divide-gray-100 max-h-[70vh] overflow-y-auto">
                {tenants.map(tenant => {
                  const isSelected = selectedTenant?.group_id === tenant.group_id
                  return (
                    <button key={tenant.group_id}
                      onClick={() => { setSelectedTenant(tenant); loadTenantDetail(tenant.group_id) }}
                      className={`w-full text-left px-4 py-3 transition-all ${isSelected ? 'bg-indigo-50 border-l-4 border-l-indigo-500 pl-3' : 'hover:bg-gray-50 border-l-4 border-l-transparent'}`}>
                      <div className="flex items-center justify-between">
                        <div>
                          <div className="text-sm font-medium text-gray-900">{tenant.name}</div>
                          <div className="text-xs text-gray-500">{tenant.subdomain}.paysafego.net</div>
                        </div>
                        <div className="flex flex-col items-end gap-1">
                          <span className={`inline-flex px-2 py-0.5 text-xs font-medium rounded-full border ${TIER_COLORS[tenant.tier || 'standard']}`}>
                            {tenant.tier || 'standard'}
                          </span>
                          <span className={`inline-flex items-center px-1.5 py-0.5 text-xs rounded-full ${tenant.status === 'active' ? 'bg-green-50 text-green-600' : 'bg-gray-100 text-gray-500'}`}>
                            {tenant.status || 'active'}
                          </span>
                        </div>
                      </div>
                    </button>
                  )
                })}
                {tenants.length === 0 && <div className="px-4 py-8 text-center text-sm text-gray-400">No tenants found</div>}
              </div>
            </div>
          </div>

          {/* Tenant detail */}
          <div className="lg:col-span-8">
            {selectedTenant ? (
              <div>
                {/* Tenant header card */}
                <div className="bg-white rounded-xl border border-gray-200 p-5 mb-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <h3 className="text-xl font-bold text-gray-900">{selectedTenant.name}</h3>
                      <p className="text-sm text-gray-500 mt-0.5">{selectedTenant.subdomain}.paysafego.net</p>
                    </div>
                    <div className="flex items-center gap-3">
                      <div>
                        <label className="block text-xs text-gray-500 mb-1">Subscription Tier</label>
                        <div className="flex items-center gap-1">
                          {TIERS.map(t => (
                            <button key={t} onClick={() => { setTenantTier(selectedTenant.group_id, t); setSelectedTenant({...selectedTenant, tier: t}) }}
                              className={`px-3 py-1.5 text-xs font-medium rounded-lg border-2 transition-all ${
                                (selectedTenant.tier || 'standard') === t
                                  ? `${TIER_COLORS[t]} border-current shadow-sm`
                                  : 'border-gray-200 text-gray-400 hover:border-gray-300'
                              }`}>
                              {t.charAt(0).toUpperCase() + t.slice(1)}
                            </button>
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>
                  {tenantFlags && (
                    <div className="mt-4 flex items-center gap-6 pt-3 border-t border-gray-100">
                      <div className="text-center">
                        <div className="text-lg font-bold text-green-600">{tenantFlags.flagDetails?.filter(f => f.enabled).length || 0}</div>
                        <div className="text-xs text-gray-500">Enabled</div>
                      </div>
                      <div className="text-center">
                        <div className="text-lg font-bold text-red-500">{tenantFlags.flagDetails?.filter(f => !f.enabled).length || 0}</div>
                        <div className="text-xs text-gray-500">Disabled</div>
                      </div>
                      <div className="text-center">
                        <div className="text-lg font-bold text-amber-500">{tenantFlags.flagDetails?.filter(f => f.hasOverride).length || 0}</div>
                        <div className="text-xs text-gray-500">Overridden</div>
                      </div>
                      <div className="text-center">
                        <div className="text-lg font-bold text-gray-400">{tenantFlags.flagDetails?.filter(f => !f.tierAllowed).length || 0}</div>
                        <div className="text-xs text-gray-500">Tier-locked</div>
                      </div>
                    </div>
                  )}
                </div>

                {/* Search */}
                <div className="mb-4">
                  <div className="relative">
                    <MagnifyingGlassIcon className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                    <input type="text" placeholder="Search flags..." value={tenantSearch} onChange={e => setTenantSearch(e.target.value)}
                      className="pl-9 pr-3 py-2 w-full text-sm border border-gray-300 rounded-xl focus:ring-indigo-500 focus:border-indigo-500" />
                  </div>
                </div>

                {/* Flags by module */}
                <div className="space-y-3">
                  {Object.entries(tenantFlagsByModule).map(([mod, modFlags]) => {
                    const Icon = MODULE_ICONS[mod] || GlobeAltIcon
                    const moduleFlag = modFlags.find(f => f.scope === 'module')
                    const pageItems = modFlags.filter(f => f.scope !== 'module')
                    const enabledCount = modFlags.filter(f => f.enabled).length

                    return (
                      <div key={mod} className="bg-white rounded-xl border border-gray-200 overflow-hidden">
                        {/* Module header with toggle */}
                        <div className="flex items-center justify-between px-4 py-3 bg-gray-50 border-b border-gray-100">
                          <div className="flex items-center gap-2">
                            <Icon className="h-5 w-5 text-gray-500" />
                            <span className="text-sm font-semibold text-gray-800 capitalize">{mod.replace('-', ' ')}</span>
                            <span className="text-xs text-gray-400">{enabledCount}/{modFlags.length}</span>
                          </div>
                          {moduleFlag && (
                            <div className="flex items-center gap-2">
                              {moduleFlag.hasOverride && (
                                <button onClick={() => removeTenantOverride(selectedTenant.group_id, moduleFlag.flagKey)}
                                  className="text-xs text-amber-600 hover:text-amber-800">Reset</button>
                              )}
                              <Toggle enabled={moduleFlag.enabled}
                                onChange={() => toggleTenantOverride(selectedTenant.group_id, moduleFlag.flagKey, moduleFlag.enabled)}
                                disabled={!moduleFlag.tierAllowed} />
                            </div>
                          )}
                        </div>

                        {/* Page-level flags */}
                        <div className="divide-y divide-gray-50">
                          {pageItems.map(flag => (
                            <div key={flag.flagKey} className={`flex items-center justify-between px-4 py-2.5 transition-colors ${!flag.tierAllowed ? 'opacity-40' : 'hover:bg-gray-50'}`}>
                              <div className="flex items-center gap-3 min-w-0 flex-1">
                                <Toggle enabled={flag.enabled} size="sm"
                                  onChange={() => toggleTenantOverride(selectedTenant.group_id, flag.flagKey, flag.enabled)}
                                  disabled={!flag.tierAllowed} />
                                <div className="min-w-0">
                                  <div className={`text-sm ${flag.enabled ? 'text-gray-900' : 'text-gray-400'}`}>{flag.name}</div>
                                </div>
                              </div>
                              <div className="flex items-center gap-2 ml-3">
                                {!flag.tierAllowed && (
                                  <span className="inline-flex items-center gap-0.5 text-xs text-gray-400">
                                    <LockClosedIcon className="h-3 w-3" /> {flag.tier}
                                  </span>
                                )}
                                {flag.hasOverride && (
                                  <>
                                    <span className="inline-flex px-1.5 py-0.5 text-xs rounded-full bg-amber-50 text-amber-700 border border-amber-200">overridden</span>
                                    <button onClick={() => removeTenantOverride(selectedTenant.group_id, flag.flagKey)}
                                      className="text-xs text-gray-400 hover:text-gray-600">Reset</button>
                                  </>
                                )}
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )
                  })}
                </div>
              </div>
            ) : (
              <div className="flex items-center justify-center h-80 bg-white rounded-xl border border-gray-200 text-gray-400">
                <div className="text-center">
                  <BuildingOfficeIcon className="h-16 w-16 mx-auto mb-3 text-gray-300" />
                  <p className="text-lg font-medium text-gray-500">Select a tenant</p>
                  <p className="text-sm mt-1">Choose a tenant from the list to manage their feature flags</p>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  )
}
